/* ==========================================================
   HAUS PDP  ·  shared behavior for all haus-pdp-* sections
   Loaded deferred by every PDP section; guarded so it runs once.
   Rules (see CLAUDE.md):
   - never reimplement the calculator (it lives in the
     _1-tile-product-form block and its own assets snippet)
   - no scroll listeners; scroll-driven effects use
     IntersectionObserver only
   - event delegation on document so later sections need no wiring
   ========================================================== */
(function () {
  'use strict';
  if (window.HausPDP) return;
  window.HausPDP = true;

  /* ---------- commerce intent event bridge ----------
     One documented payload reaches both a DOM CustomEvent and dataLayer.
     Downstream Klaviyo, Meta, or analytics wiring can subscribe without
     coupling this theme to any vendor SDK. */
  function newEventId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
    return String(Date.now()) + '-' + Math.random().toString(16).slice(2);
  }

  function emitCommerceEvent(name, payload) {
    var eventDetail = Object.assign({
      event_name: name,
      event_id: newEventId(),
      occurred_at: new Date().toISOString(),
      schema_version: '1.0'
    }, payload || {});

    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: name }, eventDetail));
    } catch (error) {
      console.warn('[HAUS analytics] dataLayer unavailable:', error);
    }
    try {
      document.dispatchEvent(new CustomEvent('haus:commerce', { detail: eventDetail }));
    } catch (error) {
      console.warn('[HAUS analytics] event dispatch unavailable:', error);
    }
    return eventDetail.event_id;
  }

  window.HausCommerceEvents = window.HausCommerceEvents || {};
  window.HausCommerceEvents.emit = emitCommerceEvent;

  /* ---------- gallery: thumbnail switching ---------- */
  document.addEventListener('click', function (e) {
    var control = e.target.closest('[data-haus-gallery-control]');
    if (control) {
      var rail = control.closest('[data-haus-gallery-rail]');
      var scroller = rail && rail.querySelector('[data-haus-gallery]');
      if (!scroller) return;
      var direction = control.dataset.hausGalleryControl === 'prev' ? -1 : 1;
      scroller.scrollBy({
        left: direction * Math.max(scroller.clientWidth * 0.8, 68),
        behavior: 'smooth'
      });
      return;
    }

    var thumb = e.target.closest('.haus-hero__thumb');
    if (!thumb) return;
    var gallery = thumb.closest('.haus-hero__gal');
    if (!gallery) return;
    var main = gallery.querySelector('[data-haus-gallery-main]');
    if (!main || !thumb.dataset.full) return;
    gallery.querySelectorAll('.haus-hero__thumb').forEach(function (t) {
      t.classList.remove('is-on');
    });
    thumb.classList.add('is-on');
    main.src = thumb.dataset.full;
    main.removeAttribute('srcset');
    main.dataset.hausGalleryIndex = thumb.dataset.hausGalleryIndex;
    thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  });

  document.querySelectorAll('[data-haus-gallery-rail]').forEach(function (rail) {
    var scroller = rail.querySelector('[data-haus-gallery]');
    if (!scroller) return;
    var updateOverflow = function () {
      rail.classList.toggle('is-overflowing', scroller.scrollWidth > scroller.clientWidth + 1);
    };
    updateOverflow();
    if ('ResizeObserver' in window) {
      var resizeObserver = new ResizeObserver(updateOverflow);
      resizeObserver.observe(scroller);
    }
  });

  /* ---------- ORDER TILE routing ----------
     Scroll to the calculator in the hero, expand it if collapsed,
     focus the area input. Never adds to cart. */
  function goToCalculator() {
    var calc = document.querySelector('[data-haus-calc]');
    if (!calc) return;
    var toggle = calc.querySelector('.tile-order-toggle');
    if (toggle && toggle.getAttribute('aria-expanded') === 'false') {
      toggle.click();
    }
    calc.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setTimeout(function () {
      var area = calc.querySelector('[ref="areaInput"]');
      if (area) area.focus({ preventScroll: true });
    }, 600);
  }

  /* ---------- BUY SAMPLE routing ---------- */
  function goToSampleBuilder(variantId, source) {
    if (!/^\d+$/.test(String(variantId))) return;
    var form = document.querySelector('[data-haus-calc] tile-product-form');
    emitCommerceEvent('haus_sample_selected', {
      intent: 'sample',
      product_id: form ? String(form.dataset.productId || '') : '',
      product_handle: form ? form.dataset.productHandle || '' : '',
      product_title: form ? form.dataset.productTitle || '' : '',
      variant_id: String(variantId),
      sku: form ? form.dataset.sampleSku || '' : '',
      source_component: source || 'pdp_cta'
    });
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var builderUrl = new URL(root + 'products/sample-bundle', window.location.origin);
    builderUrl.searchParams.set('sample', String(variantId));
    window.location.assign(builderUrl.toString());
  }

  /* CTA delegation: used by the haus-section-cta snippet and sticky bar. */
  document.addEventListener('click', function (e) {
    var order = e.target.closest('.haus-order-cta');
    if (order) {
      var target = order.dataset.target || 'calculator';
      if (target === 'url' && order.dataset.url) {
        window.location = order.dataset.url;
      } else {
        goToCalculator();
      }
      return;
    }
    var sample = e.target.closest('.haus-sample-cta');
    if (sample && sample.dataset.variant) {
      e.preventDefault();
      goToSampleBuilder(sample.dataset.variant, sample.dataset.hausIntentSource || 'pdp_cta');
    }
  });

  /* ---------- scroll-driven opacity via IntersectionObserver ----------
     Non-negotiable #9: iOS Safari throttles scroll listeners during
     momentum scrolling, so opacity is driven purely by intersection
     ratio callbacks; the CSS transition smooths between steps.
     Usage: add data-haus-fade to any element. */
  var thresholds = [];
  for (var i = 0; i <= 20; i++) thresholds.push(i / 20);

  var fadeObserver = null;
  function observeFades(root) {
    var els = (root || document).querySelectorAll('[data-haus-fade]');
    if (!els.length) return;
    if (!('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.style.opacity = 1; });
      return;
    }
    if (!fadeObserver) {
      fadeObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            var ratio = entry.intersectionRatio;
            var opacity = Math.max(0.14, Math.min(1, ratio * 1.4));
            entry.target.style.opacity = opacity.toFixed(3);
          });
        },
        { threshold: thresholds, rootMargin: '-10% 0px -10% 0px' }
      );
    }
    els.forEach(function (el) { fadeObserver.observe(el); });
  }

  /* ---------- rooms: flip cards ---------- */
  document.addEventListener('click', function (e) {
    var card = e.target.closest('.haus-card');
    if (card) card.classList.toggle('is-flip');
  });
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    var card = e.target.closest && e.target.closest('.haus-card');
    if (card) {
      e.preventDefault();
      card.classList.toggle('is-flip');
    }
  });

  /* ---------- sourcing map: pins, caption panel, tap list ----------
     The caption is a static block below the map (not a floating overlay),
     so it can never clip against .haus-mapbox's overflow:hidden. It keeps
     showing the last-selected country on mouseleave instead of hiding,
     since an editorial caption reads better staying put than flickering
     empty; only the pin ring highlight resets. Tap list below covers
     narrow screens. */
  function initMaps() {
    var boxes = document.querySelectorAll('.haus-mapbox');
    [].forEach.call(boxes, function (box) {
      if (box.dataset.hausInit) return;
      box.dataset.hausInit = '1';
      var tip = box.querySelector('.haus-tip');
      var tipC = box.querySelector('.haus-tip__c');
      var tipT = box.querySelector('.haus-tip__t');
      var pins = [].slice.call(box.querySelectorAll('.haus-pin'));
      var section = box.closest('.haus-pdp');
      var list = section ? section.querySelector('.haus-map-list') : null;
      if (!tip || !pins.length) return;

      function show(pin) {
        pins.forEach(function (q) { q.classList.remove('is-on'); });
        pin.classList.add('is-on');
        tipC.textContent = pin.getAttribute('data-c');
        tipT.innerHTML = pin.getAttribute('data-t');
        tip.classList.add('is-on');
        if (list) {
          [].forEach.call(list.children, function (b) {
            b.classList.toggle('is-on', b.dataset.c === pin.getAttribute('data-c'));
          });
        }
      }

      pins.forEach(function (pin) {
        pin.addEventListener('mouseenter', function () { show(pin); });
        pin.addEventListener('click', function () { show(pin); });
      });
      if (list) {
        [].forEach.call(list.children, function (b) {
          b.addEventListener('click', function () {
            var match = pins.filter(function (q) {
              return q.getAttribute('data-c') === b.dataset.c;
            })[0];
            if (match) show(match);
          });
        });
      }
      box.addEventListener('mouseleave', function () {
        pins.forEach(function (q) { q.classList.remove('is-on'); });
      });
      show(pins[Math.min(5, pins.length - 1)]);
    });
  }

  /* ---------- sticky bottom bar ----------
     Shown once the hero's sentinel scrolls above the viewport.
     IntersectionObserver only, per non-negotiable #9. */
  var stickyObserver = null;
  function initSticky() {
    var bar = document.querySelector('[data-haus-sticky]');
    var sentinel = document.querySelector('[data-haus-sticky-sentinel]');
    if (!bar || !sentinel || !('IntersectionObserver' in window)) return;
    if (stickyObserver) stickyObserver.disconnect();
    stickyObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var on = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          bar.classList.toggle('is-on', on);
          bar.setAttribute('aria-hidden', on ? 'false' : 'true');
        });
      },
      { threshold: 0 }
    );
    stickyObserver.observe(sentinel);
  }

  /* ---------- support bubble collision clearance ---------- */
  var chatObserver = null;
  function initChatClearance() {
    var selectors = [
      '#ShopifyChat',
      'shopify-chat',
      '[data-shopify-chat]',
      'iframe[title*="chat" i]',
      'iframe[src*="chat" i]'
    ];
    var update = function () {
      var chat = document.querySelector(selectors.join(','));
      document.documentElement.classList.toggle('haus-chat-present', Boolean(chat));
    };
    update();
    if (!document.body || chatObserver) return;
    chatObserver = new MutationObserver(update);
    chatObserver.observe(document.body, { childList: true, subtree: true });
  }
  /* ---------- account sign-in ----------
     New customer accounts require Shopify's emailed one-time code. The
     optional Shop sign-in is hidden on HAUS PDPs so customers see one clear
     path instead of being asked to verify both Shop and email. */

  function simplifyAccountSignIn(root) {
    var scope = root || document;
    var signInGroups = scope.querySelectorAll('.account-actions__sign-ins');
    signInGroups.forEach(function (group) {
      var shopLogin = group.querySelector('.account-actions__shop-login');
      if (shopLogin) {
        shopLogin.hidden = true;
        shopLogin.style.display = 'none';
      }

      var accountLogin = group.querySelector('a.account-actions__link');
      if (!accountLogin) return;

      var shopText = accountLogin.querySelector('.account-actions__sign-in-text');
      var translatedLoginText = accountLogin.querySelector('.account-actions__fallback-text');
      if (shopText) {
        shopText.hidden = true;
        shopText.style.display = 'none';
      }
      if (translatedLoginText) translatedLoginText.style.display = 'block';
    });
  }

  /* ---------- boot, plus theme editor re-renders ---------- */
  function init() {
    observeFades(document);
    initSticky();
    initMaps();
    initChatClearance();
    simplifyAccountSignIn(document);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', function (e) {
    observeFades(e.target);
    initSticky();
    initMaps();
    initChatClearance();
    simplifyAccountSignIn(document);
  });
})();
